/**
 * The composer, opened on a note to quote, with a relay hint attached.
 *
 * A separate component because finding the hint is asynchronous and `Composer`
 * must not learn how to read the store — it takes an event and a hint, which is
 * what keeps it usable from a surface that has neither.
 *
 * ## Why the hint is worth a component of its own
 *
 * A quote is a reference handed to strangers. `nevent` without a relay is an event
 * id and an author, and resolving it then depends on the reader's relay set
 * happening to overlap ours — which for a quote of someone outside both readers'
 * usual relays is exactly when it does not. The hint is the difference between an
 * embedded note and an "unavailable" placeholder in someone else's client, so it
 * comes from a relay we have *actually seen this note on* rather than from our own
 * configured list.
 */

import type { NostrEvent } from "@setu/protocol";
import { useEffect, useState } from "react";
import { useEngine } from "../../engine/EngineProvider";
import { Composer } from "../compose/Composer";

export interface QuoteComposerProps {
  target: NostrEvent;
  authorName?: string;
  onCancel?(): void;
  onPosted?(): void;
  className?: string;
}

export function QuoteComposer({
  target,
  authorName,
  onCancel,
  onPosted,
  className,
}: QuoteComposerProps) {
  const engine = useEngine();
  /*
   * Undefined until the local lookup lands, and the composer is usable throughout.
   *
   * The read is a local store hit, so in practice it resolves long before anyone
   * finishes typing a comment. Blocking the box on it anyway would trade a rare
   * missing hint — which only degrades the reference, never breaks it — for a
   * composer that stutters on open every time.
   */
  const [relayHint, setRelayHint] = useState<string | undefined>();

  useEffect(() => {
    let live = true;
    void (async () => {
      const stored = await engine.store.get(target.id).catch(() => undefined);
      // Provenance first: somewhere the note demonstrably is. Our own first relay
      // is the fallback, and a weaker claim — we may simply never have asked it.
      const hint = stored?.provenance.relays[0] ?? engine.relays[0];
      if (live && hint) setRelayHint(hint);
    })();
    return () => {
      live = false;
    };
  }, [engine, target.id]);

  return (
    <Composer
      autoFocus
      quote={{
        target,
        ...(relayHint ? { relayHint } : {}),
        ...(authorName ? { authorName } : {}),
      }}
      className={className}
      {...(onCancel ? { onCancel } : {})}
      {...(onPosted ? { onPosted: () => onPosted() } : {})}
    />
  );
}
